import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T, LAYERS } from "../app/tokens.js";

// Own the topmost keyboard boundary so Escape/Tab never reach a stacked editor.
export function ArchiveConfirmDialog({ isOpen, objectLabel, onConfirm, onCancel, busy, explanation }) {
  const [reason, setReason] = useState("");
  const input = useRef(null);
  const dialog = useRef(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const titleId = useId();
  const reasonId = useId();
  const descriptionId = useId();
  useEffect(() => {
    if (!isOpen) return undefined;
    const trigger = document.activeElement;
    setReason("");
    input.current?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!busyRef.current) cancelRef.current();
      }
      if (event.key === "Tab") {
        event.stopImmediatePropagation();
        const items = [...dialog.current.querySelectorAll("input:not([disabled]), button:not([disabled])")];
        if (!items.length) { event.preventDefault(); return; }
        const first = items[0], last = items.at(-1);
        if (!items.includes(document.activeElement) || (event.shiftKey && document.activeElement === first)) {
          event.preventDefault(); (event.shiftKey ? last : first).focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      if (trigger?.isConnected) trigger.focus();
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const buttonStyle = { border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 13px", fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: busy ? "wait" : "pointer" };
  return createPortal(
    <div data-testid="archive-confirm-overlay" style={{ position: "fixed", inset: 0, zIndex: LAYERS.modal + 1, background: T.modalOverlay, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onMouseDown={(event) => { if (event.target === event.currentTarget) { event.preventDefault(); if (!busy) onCancel(); } }}>
      <div ref={dialog} role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} aria-busy={busy} style={{ width: "100%", maxWidth: 420, background: T.white, color: T.dark, border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: T.shadowModal, padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <span aria-hidden="true" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, flexShrink: 0, borderRadius: "50%", background: T.redSoft, color: T.red }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" /></svg>
          </span>
          <h2 id={titleId} style={{ margin: 0, fontSize: T.fsMd5 }}>Archive this {objectLabel}?</h2>
        </div>
        <p id={descriptionId} style={{ fontSize: T.fsSm, color: T.mid, lineHeight: 1.55 }}>
          {explanation ?? <>It moves to <b>Recently Deleted</b> and drops off the active list, Timeline, Updates and Team.
          You can restore it for <b>30 days</b>. Child records are preserved.</>}
        </p>
        <form onSubmit={(event) => { event.preventDefault(); if (!busy) onConfirm(reason.trim() || null); }}>
          <label htmlFor={reasonId} style={{ display: "block", fontSize: T.fsSm, fontWeight: T.fwSemibold, marginBottom: 6 }}>Reason (optional)</label>
          <input ref={input} id={reasonId} disabled={busy} value={reason} onChange={(event) => setReason(event.target.value)} style={{ width: "100%", boxSizing: "border-box", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px", color: T.dark, background: T.white, fontSize: T.fsSm }} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
            <button type="submit" disabled={busy} style={{ ...buttonStyle, background: T.red, color: T.white, opacity: busy ? 0.6 : 1 }}>{busy ? "Archiving…" : `Archive ${objectLabel}`}</button>
            <button type="button" disabled={busy} onClick={onCancel} style={{ ...buttonStyle, color: T.mid, background: T.white }}>Cancel</button>
            <span style={{ marginLeft: "auto", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.blue }}>ADMIN ONLY</span>
          </div>
        </form>
      </div>
    </div>, document.body,
  );
}
